import React, { useEffect, useState } from "react";
import { FormControl, Input, InputLabel, makeStyles } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import PropTypes from "prop-types";
import { Redirect, useParams } from "react-router-dom";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import { MailOutline } from "@material-ui/icons";
import Check from "@material-ui/icons/Check";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput";
import { bookingApi, masterApi } from "functions/apis";
import { token } from "functions/Helper";
import SindoDialog from "components/Dialog/SindoDialog";

const useStyles = makeStyles(styles);

export function EditSectorBookingConfiguration(props) {
  const classes = useStyles();

  const editSectorBookingConfigurationSignal = React.useRef(
    new AbortController()
  );
  const { id } = useParams();

  const [sector, setSector] = useState(null);
  const [payload, setPayload] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [redirect, setRedirect] = useState(false);

  // next sector state start
  const [nextSectorList, setNextSectorList] = useState([]);
  const [nextSectorPages, setNextSectorPages] = useState(0);
  const [nextSectorPagination, setNextSectorPagination] = useState({
    pageIndex: 1,
    pageSize: 5
  });
  const [nextSectorFilter, setNextSectorFilter] = useState({
    searchString: "",
    publicationStatus: 1
  });
  const [nextSectorLoading, setNextSectorLoading] = useState(false);
  const [selectedNextSector, setSelectedNextSector] = useState(null);

  const [showNextSectorModal, setShowNextSectorModal] = useState(false);
  // next sector state end

  useEffect(() => {
    return () => editSectorBookingConfigurationSignal.current.abort();
  }, []);

  useEffect(() => {
    if (
      token["booking.sectors.read"] === "allowed" ||
      token.is_admin === "True"
    )
      bookingApi
        .get("/Sectors/" + id, {
          signal: editSectorBookingConfigurationSignal.current.signal
        })
        .then(data => {
          setSector(data);
          setPayload({
            nextSectorID: data.nextSector?.id ?? null,
            hasGST: data.hasGST,
            isInternationalTravel: data.isInternationalTravel,
            tfsLookUpCode: data.tfsLookUpCode
          });
          setSelectedNextSector(data.nextSector);
        })
        .catch(error => console.log(error));
  }, [id]);

  useEffect(() => {
    setNextSectorLoading(true);
    masterApi
      .get("Sectors", {
        signal: editSectorBookingConfigurationSignal.current.signal,
        params: {
          filter: nextSectorFilter,
          pagination: nextSectorPagination
        }
      })
      .then(data => {
        setNextSectorList(
          data.records
            .filter(record => record.id !== id)
            .map(record => {
              return {
                ...record,
                actions: (
                  <div className="actions-right">
                    <a
                      onClick={() => {
                        setPayload(prev => ({
                          ...prev,
                          nextSectorID: record.id
                        }));
                        setShowNextSectorModal(false);
                        setSelectedNextSector(record);
                      }}
                    >
                      <Button color="info" className="like">
                        Select
                      </Button>
                    </a>
                  </div>
                )
              };
            })
        );
        setNextSectorPages(
          Math.ceil(data.totalRecords / nextSectorPagination.pageSize)
        );
      })
      .catch(error => console.log(error))
      .finally(() => setNextSectorLoading(false));
  }, [nextSectorFilter, nextSectorPagination]);

  const handleIsInternationalTravelChanged = e =>
    setPayload({
      ...payload,
      isInternationalTravel: e.target.checked
    });

  const handleHasGSTChanged = e =>
    setPayload({
      ...payload,
      hasGST: e.target.checked
    });

  const handleTFSLookUpCodeChanged = e =>
    setPayload({
      ...payload,
      tfsLookUpCode: e.target.value === "" ? null : e.target.value
    });

  const handleNextSectorSearchStringChanged = e =>
    setNextSectorFilter({
      ...nextSectorFilter,
      searchString: e.target.value === "" ? null : e.target.value
    });

  const handleClearNextSector = () => {
    setPayload({
      ...payload,
      nextSectorID: null
    });
    setSelectedNextSector(null);
  };

  const handleSubmit = e => {
    e.preventDefault();
    setDisabled(true);

    bookingApi
      .put("/Sectors/" + id, payload, {
        signal: editSectorBookingConfigurationSignal.current.signal
      })
      .then(() => setRedirect(true))
      .catch(error => {
        console.log(error);
        setDisabled(false);
      });
  };

  return redirect ? (
    <Redirect to={"/admin/Sector/Detail/" + id} />
  ) : (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <MailOutline />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              EDIT BOOKING CONFIGURATION
            </h4>
          </CardHeader>
          <CardBody>
            <Button onClick={() => props.history.goBack()} value="Back">
              BACK
            </Button>
            {payload !== null && (
              <form onSubmit={handleSubmit}>
                <GridContainer>
                  <GridItem xs={6}>
                    <CustomInput
                      labelText="Code"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        value: sector?.code ?? "",
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <CustomInput
                      labelText="Name"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        value: sector?.name ?? "",
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={4} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={payload.isInternationalTravel}
                          onChange={handleIsInternationalTravelChanged}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                            root: classes.checkRoot
                          }}
                        />
                      }
                      classes={{
                        label: classes.label,
                        root: classes.labelRoot
                      }}
                      label="Is International Travel"
                    />
                  </GridItem>
                  <GridItem xs={12} sm={4} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={payload.hasGST}
                          onChange={handleHasGSTChanged}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                            root: classes.checkRoot
                          }}
                        />
                      }
                      classes={{
                        label: classes.label,
                        root: classes.labelRoot
                      }}
                      label="Has GST"
                    />
                  </GridItem>
                  <GridItem xs={12} sm={4} md={4}>
                    <CustomInput
                      labelText="TFS Look Up Code"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        onChange: handleTFSLookUpCodeChanged,
                        value: payload.tfsLookUpCode ?? "",
                        inputProps: { maxLength: 50 }
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <FormControl
                      fullWidth
                      className={classes.selectFormControl}
                    >
                      <InputLabel
                        htmlFor="selNextSectorID"
                        className={classes.selectLabel}
                      >
                        Next Sector
                      </InputLabel>
                      <Input
                        onClick={() => setShowNextSectorModal(prev => !prev)}
                        className={classes.select}
                        value={
                          selectedNextSector
                            ? selectedNextSector.code +
                              " - " +
                              selectedNextSector.name
                            : ""
                        }
                      />
                      <SindoDialog
                        openModal={showNextSectorModal}
                        setOpenModal={val => setShowNextSectorModal(val)}
                        title="Next Sector"
                        pages={nextSectorPages}
                        pagination={nextSectorPagination}
                        loading={nextSectorLoading}
                        data={nextSectorList}
                        columns={[
                          {
                            id: "code",
                            Header: "Code",
                            accessor: d => d.code
                          },
                          {
                            id: "name",
                            Header: "Name",
                            accessor: d => d.name
                          },
                          {
                            Header: "Actions",
                            accessor: "actions",
                            sortable: false,
                            filterable: false
                          }
                        ]}
                        handleSearchStringChanged={
                          handleNextSectorSearchStringChanged
                        }
                        searchString={nextSectorFilter.searchString}
                        setPagination={setNextSectorPagination}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <Button
                      onClick={handleClearNextSector}
                      type="button"
                      value="Clear"
                    >
                      Clear Next Sector
                    </Button>
                  </GridItem>
                  <GridItem xs={12} md={12}>
                    <Button
                      disabled={disabled}
                      color="rose"
                      type="submit"
                      value="Submit"
                    >
                      Submit
                    </Button>
                  </GridItem>
                </GridContainer>
              </form>
            )}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

EditSectorBookingConfiguration.propTypes = {
  history: PropTypes.object
};
